import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, IconButton, CircularProgress, Divider, Tooltip, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useTranslation } from 'react-i18next';
import {
  addHealthRecord, updateHealthRecord, addHealthType, addHealthCondition,
  type HealthRecord, type HealthData,
} from '../../services/lifeDataService';

interface HealthLocation {
  lat: number;
  lng: number;
  placeName: string;
  country: string;
  timezone: string;
  fromGps?: boolean;
}

interface Props {
  open: boolean;
  editRecord?: HealthRecord;
  healthConfig: HealthData['config'];
  prefillLocation?: HealthLocation;
  prefillTs?: string;
  container?: Element | null;
  onClose: () => void;
  onSaved: (isNew: boolean) => void;
}

function localNow(timezone?: string): string {
  try {
    if (!timezone) throw new Error('no tz');
    const ts = new Intl.DateTimeFormat('sv-SE', {
      timeZone: timezone,
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date());
    return ts.replace(' ', 'T').slice(0, 16);
  } catch {
    return new Date().toISOString().slice(0, 16);
  }
}

const HealthRecordDialog: React.FC<Props> = ({
  open, editRecord, healthConfig, prefillLocation, prefillTs, container, onClose, onSaved,
}) => {
  const { t } = useTranslation();

  const types = healthConfig.types;

  // ── Form state ─────────────────────────────────────────────────────────────
  const [type, setType]           = useState('');
  const [condition, setCondition] = useState('');
  const [ts, setTs]               = useState('');
  const [timezone, setTimezone]   = useState('');
  const [values, setValues]       = useState<string[]>([]);
  const [food, setFood]           = useState('');
  const [medicine, setMedicine]   = useState('');
  const [notes, setNotes]         = useState('');
  const [location, setLocation]   = useState<HealthLocation | undefined>(undefined);

  const [newType, setNewType]       = useState('');
  const [addingType, setAddingType] = useState(false);
  const [newCond, setNewCond]       = useState('');
  const [addingCond, setAddingCond] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');

  const typeConfig  = types.find(typ => typ.name === type);
  const conditions  = typeConfig?.conditions ?? [];
  const valueSchema = typeConfig?.valueSchema ?? [];

  // ── Init / reset on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setSaveError('');
    if (editRecord) {
      setType(editRecord.type);
      setCondition(editRecord.condition);
      setTs(editRecord.ts);
      setTimezone(editRecord.timezone);
      setValues(editRecord.values.map(String));
      setFood(editRecord.food ?? '');
      setMedicine(editRecord.medicine ?? '');
      setNotes(editRecord.notes ?? '');
      setLocation(editRecord.location);
    } else {
      const initTz = prefillLocation?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      const initTs = prefillTs ?? localNow(initTz);
      setType(types[0]?.name ?? '');
      setCondition(types[0]?.conditions[0] ?? '');
      setTs(initTs);
      setTimezone(initTz);
      setValues([]);
      setFood('');
      setMedicine('');
      setNotes('');
      setLocation(prefillLocation);
    }
    setAddingType(false);
    setAddingCond(false);
    setNewType('');
    setNewCond('');
    setSaving(false);
  }, [open, editRecord, prefillLocation, prefillTs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editRecord || editRecord.type !== type) setValues([]);
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValueChange = (idx: number, val: string) => {
    setValues(prev => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const handleAddType = async () => {
    const name = newType.trim();
    if (!name) return;
    await addHealthType({ name, valueSchema: [], conditions: [] });
    setType(name);
    setCondition('');
    setNewType('');
    setAddingType(false);
  };

  const handleAddCondition = async () => {
    const cond = newCond.trim();
    if (!cond || !type) return;
    await addHealthCondition(type, cond);
    setCondition(cond);
    setNewCond('');
    setAddingCond(false);
  };

  const handleSave = async () => {
    if (!type || !ts) return;
    setSaving(true);
    setSaveError('');
    try {
      const record: Omit<HealthRecord, 'id'> = {
        ts,
        timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        type,
        condition,
        values: values.map(v => parseFloat(v) || 0),
        food,
        medicine,
        notes,
        location,
      };
      if (editRecord) {
        await updateHealthRecord(editRecord.id, record);
      } else {
        await addHealthRecord(record);
      }
      onSaved(!editRecord);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setSaveError(t('lifeCenter.healthSaveFailed', { msg }));
    } finally {
      setSaving(false);
    }
  };

  const isValid = !!type && !!ts;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      container={container}
      PaperProps={{
        sx: { borderRadius: 4, boxShadow: '0 8px 40px rgba(233,30,99,0.15)' },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle sx={{ pb: 1.5, background: 'linear-gradient(135deg, #fce4ec 0%, #fff 60%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FavoriteIcon sx={{ color: '#fff', fontSize: 20 }} />
          </div>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
            {editRecord ? t('lifeCenter.healthEditRecord') : t('lifeCenter.healthNewRecord')}
          </Typography>
          <div style={{ flex: 1 }} />
          <IconButton size="small" onClick={onClose} sx={{ color: '#9e9e9e' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2.5, pb: 2.5 }}>

        {/* ── Error alert ── */}
        {saveError && (
          <Alert severity="error" onClose={() => setSaveError('')} sx={{ borderRadius: 2 }}>
            {saveError}
          </Alert>
        )}

        {/* ── GPS / Location card ── */}
        {location && (
          <div style={{
            background: '#fce4ec', borderRadius: 10, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>{location.fromGps ? '🛰' : '📍'}</span>
            <div>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#880e4f' }}>
                {location.placeName}
                {location.country ? ` · ${location.country}` : ''}
                {location.fromGps && (
                  <span style={{
                    marginLeft: 6, fontSize: 10, fontWeight: 600,
                    background: '#e91e63', color: '#fff',
                    borderRadius: 4, padding: '1px 6px', verticalAlign: 'middle',
                  }}>GPS</span>
                )}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#ad1457', mt: 0.3 }}>
                {location.timezone}
              </Typography>
            </div>
            <div style={{ flex: 1 }} />
            <IconButton size="small" onClick={() => setLocation(undefined)}
              sx={{ color: '#f48fb1', '&:hover': { color: '#e91e63' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}

        {/* ── 紀錄時間 ── */}
        <TextField
          type="datetime-local"
          label={t('lifeCenter.healthRecordTime')}
          value={ts}
          onChange={e => setTs(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText={timezone ? `⏱ ${timezone}` : undefined}
          sx={{ '& .MuiFormHelperText-root': { fontSize: 11, color: '#e91e63', mt: 0.4 } }}
        />

        <Divider />

        {/* ── 類型 ── */}
        <div>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9e9e9e', mb: 1 }}>
            {t('lifeCenter.healthTypeCondition')}
          </Typography>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>{t('lifeCenter.healthType')}</InputLabel>
              <Select
                value={type}
                label={t('lifeCenter.healthType')}
                onChange={e => { setType(e.target.value); setCondition(''); }}
                MenuProps={{ disablePortal: true }}
              >
                {types.map(typ => (
                  <MenuItem key={typ.name} value={typ.name}>{typ.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {addingType ? (
              <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder={t('lifeCenter.healthNewTypeName')}
                  value={newType}
                  onChange={e => setNewType(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddType()}
                  autoFocus
                />
                <Button variant="contained" onClick={handleAddType}
                  sx={{ background: '#e91e63', '&:hover': { background: '#c2185b' }, minWidth: 52 }}>
                  {t('lifeCenter.healthAddBtn')}
                </Button>
                <Button onClick={() => setAddingType(false)} sx={{ color: '#9e9e9e', minWidth: 36 }}>✕</Button>
              </div>
            ) : (
              <Tooltip title={t('lifeCenter.healthAddTypeTooltip')}>
                <IconButton onClick={() => setAddingType(true)}
                  sx={{ color: '#e91e63', border: '1px dashed #f48fb1', borderRadius: 2, mt: 0.5 }}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>

        {/* ── 狀態/條件 ── */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <FormControl sx={{ flex: 1 }} disabled={conditions.length === 0}>
            <InputLabel>{t('lifeCenter.healthCondition')}</InputLabel>
            <Select
              value={condition}
              label={t('lifeCenter.healthCondition')}
              onChange={e => setCondition(e.target.value)}
              MenuProps={{ disablePortal: true }}
            >
              {conditions.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {addingCond ? (
            <div style={{ display: 'flex', gap: 6, flex: 1 }}>
              <TextField
                fullWidth
                placeholder={t('lifeCenter.healthNewCondName')}
                value={newCond}
                onChange={e => setNewCond(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddCondition()}
                autoFocus
              />
              <Button variant="contained" onClick={handleAddCondition}
                sx={{ background: '#e91e63', '&:hover': { background: '#c2185b' }, minWidth: 52 }}>
                {t('lifeCenter.healthAddBtn')}
              </Button>
              <Button onClick={() => setAddingCond(false)} sx={{ color: '#9e9e9e', minWidth: 36 }}>✕</Button>
            </div>
          ) : (
            <Tooltip title={t('lifeCenter.healthAddCondTooltip')}>
              <IconButton onClick={() => setAddingCond(true)} disabled={!type}
                sx={{ color: '#e91e63', border: '1px dashed #f48fb1', borderRadius: 2, mt: 0.5 }}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {/* ── 數值輸入 ── */}
        {valueSchema.length > 0 && (
          <>
            <Divider />
            <div>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9e9e9e', mb: 1.5 }}>
                {t('lifeCenter.healthMeasuredValues')}
              </Typography>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {valueSchema.map((schema, idx) => (
                  <TextField
                    key={idx}
                    type="number"
                    label={`${schema.label} (${schema.unit})`}
                    value={values[idx] ?? ''}
                    onChange={e => handleValueChange(idx, e.target.value)}
                    inputProps={{ step: 'any', style: { fontWeight: 700, fontSize: 22, textAlign: 'center' } }}
                    sx={{ flex: '1 1 130px', minWidth: 130 }}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* ── 飲食 / 用藥 / 備註 ── */}
        <div>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9e9e9e', mb: 1.5 }}>
            {t('lifeCenter.healthAdditionalInfo')}
          </Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <TextField
              label={t('lifeCenter.healthFoodLabel')}
              value={food}
              onChange={e => setFood(e.target.value)}
              size="small" fullWidth
              placeholder={t('lifeCenter.healthFoodPh')}
            />
            <TextField
              label={t('lifeCenter.healthMedicineLabel')}
              value={medicine}
              onChange={e => setMedicine(e.target.value)}
              size="small" fullWidth
              placeholder={t('lifeCenter.healthMedicinePh')}
            />
            <TextField
              label={t('lifeCenter.healthNotesLabel')}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              size="small" fullWidth multiline rows={2}
              placeholder={t('lifeCenter.healthNotesPh')}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#9e9e9e' }}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
          sx={{
            background: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #c2185b 0%, #e91e63 100%)' },
            '&.Mui-disabled': { background: '#e0e0e0' },
            fontWeight: 700, borderRadius: 2, px: 3,
          }}
        >
          {saving ? t('lifeCenter.healthSaving') : t('lifeCenter.healthSaveRecord')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HealthRecordDialog;
